import {
    LoadingIndicator,
    Logout,
    AppBar as ReactAppBar,
    ToggleThemeButton,
    useAuthProvider,
    useLocaleState,
    usePermissions,
    UserMenuContext,
    useTheme,
} from "react-admin";
import Typography from "@mui/material/Typography";
import logoLight from "shared/src/imgs/logo-light.png";
import logoDark from "shared/src/imgs/logo-dark.png";
import { useCallback, useEffect, useMemo, useState } from "react";
import { IconButton, Menu, Tooltip } from "@mui/material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import { MeowLogo } from "shared/src/components/layout/MeowLogo";

const AppBar = (props) => {
    const [locale, setLocale] = useLocaleState();
    const [headerStyles, setHeaderStyles] = useState({
        mandantName: "",
        mandantColor: "",
    });
    const { permissions } = usePermissions();
    const [theme] = useTheme();

    useEffect(() => {
        const userLocale = permissions?.user?.locale;
        const userLanguage = userLocale?.split("_")[0];

        if (userLanguage && locale !== userLanguage) {
            setLocale(userLanguage);
        }
    }, [locale, setLocale, permissions]);

    useEffect(() => {
        const getInstanceName = async () => {
            const instanceName = await permissions?.config?.getAsync("mandantName");
            const instanceColor = await permissions?.config?.getAsync("mandantColor");
            setHeaderStyles({
                mandantName: instanceName,
                mandantColor: instanceColor,
            });
        };
        getInstanceName();
    }, [permissions?.config]);

    return (
        <ReactAppBar
            {...props}
            userMenu={false}
            sx={{
                backgroundColor: theme === "light" &&
                    headerStyles.mandantColor,
                }}
            toolbar={
                <>
                    <ToggleThemeButton />
                    <LoadingIndicator />
                    <TitledUserMenu />
                </>
            }
        >
            <MeowLogo
                src={theme === "dark" ? logoDark : logoLight}
                sx={{ marginRight: "0.5rem", marginLeft: "1rem" }}
            />
            <Typography
                sx={{
                    fontFamily: "Maven Pro, sans-serif",
                    fontWeight: 500,
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    marginRight: "2rem",
                    color: theme === "dark" ? headerStyles.mandantColor : "#000",
                }}
            >
                {headerStyles.mandantName}
            </Typography>
            <Typography
                variant="h6"
                id="react-admin-title"
                sx={{
                    color: "inherit",
                    flex: 1,
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden"
                }} />
        </ReactAppBar>
    );
};

const TitledUserMenu = (props) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const authProvider = useAuthProvider();
    const { permissions } = usePermissions();
    const user = permissions?.user;
    const tooltip =
        user &&
        `${user.firstname} ${user.lastname} \n ${user.username} \n ${user.roles
            .map((role) => role)
            .join("\n")}`;

    const { children = !!authProvider ? <Logout /> : null, icon = <AccountCircle /> } = props;

    const handleMenu = (event) => setAnchorEl(event.currentTarget);
    const handleClose = useCallback(() => setAnchorEl(null), []);
    const context = useMemo(() => ({ onClose: handleClose }), [handleClose]);
    if (!children) return null;
    const open = Boolean(anchorEl);

    return (
        <div>
            <Tooltip
                title={<div style={{ whiteSpace: "pre-line", lineHeight: "1.8" }}>{tooltip}</div>}
            >
                <IconButton
                    aria-label={"hey"}
                    aria-owns={open ? "menu-appbar" : undefined}
                    aria-haspopup={true}
                    color="inherit"
                    onClick={handleMenu}
                >
                    {icon}
                </IconButton>
            </Tooltip>
            <UserMenuContextProvider value={context}>
                <Menu
                    id="menu-appbar"
                    disableScrollLock
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    open={open}
                    onClose={handleClose}
                >
                    {children}
                </Menu>
            </UserMenuContextProvider>
        </div>
    );
};

export const UserMenuContextProvider = ({ children, value }) => (
    <UserMenuContext.Provider value={value}>{children}</UserMenuContext.Provider>
);

export default AppBar;
